import { useContext, useEffect } from 'react'
import { Formik } from 'formik'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router-dom'

import Grid from '@mui/material/Grid'
import Button from '@mui/material/Button'
import { Typography, useMediaQuery, useTheme } from '@mui/material'

import {
  AppVersion,
  AuthContentWrapper,
  AuthFormWrapper,
  AuthHero,
  HelperWrapper,
  Recaptcha,
} from 'src/components'

import {
  useAuthenticatedRedirect,
  useLogin,
  useSignUpSettings,
  useTokenJourneyRedirect,
  useVerification,
} from 'src/hooks'
import { IUserLogin } from 'src/types/auth.type'

import { ThemeSwitchContext } from 'src/providers/theme-provider'
import { ROUTES, SPACES } from 'src/constants'
import useStyles from '../styles'
import { signInSchema } from 'src/common/validations'
import { DEFAULT_LANGUAGE_CODE } from 'src/components/languages/translations-list/constants'
import { SignInFormV2 } from 'src/components/sign-in/v2/SignInFormV2'
import { useSnackbar } from 'notistack'

const SignInScreen = () => {
  const { i18n } = useTranslation()
  const { mutateAsync: login } = useLogin()
  const { token } = useParams()
  const { setWhiteLable } = useContext(ThemeSwitchContext)
  const { captchaRef, verifyAsync, verificationResp } = useVerification()
  const { data: settings, isLoading } = useSignUpSettings()
  const classes = useStyles()
  const theme = useTheme()
  const isLaptop = useMediaQuery(theme.breakpoints.down('laptop'))

  const { enqueueSnackbar } = useSnackbar()

  useAuthenticatedRedirect()
  const { isJourneyTokenAuthLoading, journeyRedirect } = useTokenJourneyRedirect()

  useEffect(() => {
    setWhiteLable(undefined)
  }, [setWhiteLable])

  const handleLanguageSwitch = () => {
    i18n.changeLanguage(DEFAULT_LANGUAGE_CODE)
  }

  const handleLogin = async (values: IUserLogin) => {
    if (!verificationResp) {
      const isVerified = await verifyAsync()

      if (!isVerified) {
        enqueueSnackbar('Verification failed. Please try again', { variant: 'error' })

        return
      }
    }

    await login(values)
  }

  return (
    <>
      <AuthContentWrapper>
        <AuthHero />

        <Grid
          px={isLaptop ? SPACES.xxxl : 0}
          item
          xs={isLaptop ? 12 : 6}
          className={classes.contentWrapper}
        >
          {settings?.sign_up_enabled && (
            <Grid
              display={isLaptop ? 'none' : 'flex'}
              justifyContent="flex-end"
              className={classes.signUpContainer}
            >
              <Typography className={classes.textSignUp} alignItems={'center'}>
                <HelperWrapper t={'login-opposite-text'} />{' '}
                <Link
                  style={{ textDecoration: 'none', textTransform: 'none' }}
                  to={token ? `${ROUTES.REGISTER}/${token}` : ROUTES.REGISTER}
                >
                  <Button className={classes.buttonSignUp}>
                    <HelperWrapper t="signup-button-text" />
                  </Button>
                </Link>
              </Typography>
            </Grid>
          )}
          <AuthFormWrapper>
            <Formik
              initialValues={{
                email: '',
                password: '',
                rememberMe: false,
              }}
              onSubmit={handleLogin}
              validationSchema={signInSchema}
            >
              {/* <SignInForm settings={settings || null} /> */}
              <SignInFormV2
                captcha={{ verifyAsync: verifyAsync, verificationResp: verificationResp }}
                journeyRedirect={journeyRedirect}
                isJourneyTokenAuthLoading={isJourneyTokenAuthLoading}
                token={token}
                isLoading={!settings || isLoading}
              />
            </Formik>
          </AuthFormWrapper>
          <footer className={classes.footerWrapper}>
            {i18n.language !== DEFAULT_LANGUAGE_CODE && (
              <Button onClick={handleLanguageSwitch}>Switch to English</Button>
            )}
            <AppVersion />
          </footer>
        </Grid>
      </AuthContentWrapper>
      <Recaptcha captchaRef={captchaRef} />
    </>
  )
}

export default SignInScreen
